/**
 * The run-scope library's addresses (ADR 6028): which market, which kind, which entry, which
 * change set. Each is on the link (CLAUDE.md §3.4) and parsed from a string, so a test and an export
 * resolver read the same thing the page reads. The market is never defaulted but by ADR 9056 (the
 * only market a person can see).
 */

import { useEffect, useState } from 'react';

import { isEntryKind, type EntryKind } from './model';

export const RUN_SCOPES_PATH = '/inseason/run-scopes';
export const RUN_SCOPE_EDIT_PATH = `${RUN_SCOPES_PATH}/edit`;
export const RUN_SCOPE_CHANGES_PATH = `${RUN_SCOPES_PATH}/changes`;
/** The parameters' queue opened on Management, where a library change set waits (its items fall to Management). */
export const RUN_SCOPE_APPROVALS_HREF = '/foundations/parameters/approvals?app=management';

export const MARKET_PARAM = 'market';
export const KIND_PARAM = 'kind';
export const ENTRY_PARAM = 'entry';

export interface LibraryScope {
  readonly market: string | null;
  readonly kind: EntryKind | null;
  readonly entry: string | null;
}

function text(params: URLSearchParams, name: string): string | null {
  const value = params.get(name)?.trim() ?? '';
  return value === '' ? null : value;
}

export function readLibraryScope(search: string): LibraryScope {
  const params = new URLSearchParams(search);
  const kind = text(params, KIND_PARAM);
  return { market: text(params, MARKET_PARAM), kind: isEntryKind(kind) ? kind : null, entry: text(params, ENTRY_PARAM) };
}

/** `/inseason/run-scopes/{kind}/{entryId}` → its kind and id; `null` for any other path. */
export function readEntryPath(pathname: string): { readonly kind: EntryKind; readonly entryId: string } | null {
  const rest = pathname.startsWith(`${RUN_SCOPES_PATH}/`) ? pathname.slice(RUN_SCOPES_PATH.length + 1).split('/') : [];
  if (rest.length !== 2) return null;
  const [kind, entryId] = rest.map((part) => decodeURIComponent(part)) as [string, string];
  return isEntryKind(kind) && entryId !== '' ? { kind, entryId } : null;
}

/** `/inseason/run-scopes/changes/{changeId}` → its id; `null` for any other path. */
export function readChangePath(pathname: string): string | null {
  if (!pathname.startsWith(`${RUN_SCOPE_CHANGES_PATH}/`)) return null;
  const id = decodeURIComponent(pathname.slice(RUN_SCOPE_CHANGES_PATH.length + 1));
  return id === '' || id.includes('/') ? null : id;
}

function withMarket(path: string, market: string, extra: Readonly<Record<string, string>> = {}): string {
  return `${path}?${new URLSearchParams({ [MARKET_PARAM]: market, ...extra }).toString()}`;
}

export function libraryHref(market: string, kind: EntryKind | null = null): string {
  return withMarket(RUN_SCOPES_PATH, market, kind === null ? {} : { [KIND_PARAM]: kind });
}

export function entryHref(market: string, kind: EntryKind, entryId: string): string {
  return withMarket(`${RUN_SCOPES_PATH}/${encodeURIComponent(kind)}/${encodeURIComponent(entryId)}`, market);
}

export function editHref(market: string, kind: EntryKind, entryId: string | null = null): string {
  return withMarket(RUN_SCOPE_EDIT_PATH, market, { [KIND_PARAM]: kind, ...(entryId === null ? {} : { [ENTRY_PARAM]: entryId }) });
}

export function changeHref(changeId: string): string {
  return `${RUN_SCOPE_CHANGES_PATH}/${encodeURIComponent(changeId)}`;
}

/** The address bar, following back and forward. */
export function useAddress(): { readonly search: string; readonly pathname: string } {
  const [, setTick] = useState(0);
  useEffect(() => {
    const onPop = (): void => setTick((count) => count + 1);
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);
  return typeof window === 'undefined'
    ? { search: '', pathname: '' }
    : { search: window.location.search, pathname: window.location.pathname };
}

/** Write one key into the address, as a new history entry (Back returns to the previous choice). */
export function pushParam(name: string, value: string | null): void {
  if (typeof window === 'undefined') return;
  const next = new URLSearchParams(window.location.search);
  if (value === null || value === '') next.delete(name);
  else next.set(name, value);
  const query = next.toString();
  window.history.pushState({}, '', query === '' ? window.location.pathname : `${window.location.pathname}?${query}`);
  window.dispatchEvent(new PopStateEvent('popstate'));
}

/** Go to an in-app address, as a link would. */
export function goTo(href: string): void {
  if (typeof window === 'undefined') return;
  window.history.pushState({}, '', href);
  window.dispatchEvent(new PopStateEvent('popstate'));
}
